import { useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
} from "@mui/material";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { login, isAuthenticated, isLoading: authLoading, requiresPasswordChange, completeInitialPassword, cancelPasswordChange } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (authLoading) return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/timesheet" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (requiresPasswordChange && (newPassword.length < 15 || newPassword.length > 128 || newPassword !== confirmation)) {
      setError(newPassword !== confirmation ? "De wachtwoorden komen niet overeen." : "Gebruik een wachtwoord van 15 tot 128 tekens.");
      return;
    }
    setIsLoading(true);

    try {
      if (requiresPasswordChange) {
        await completeInitialPassword(newPassword);
        setNewPassword(""); setConfirmation("");
      } else {
        await login({ emailOrUserName: email, password });
        setPassword("");
      }
    } catch (err) {
      setError(axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message ?? "De aanvraag is mislukt. Probeer het opnieuw."
        : err instanceof Error ? err.message : "De aanvraag is mislukt. Probeer het opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Timesheet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 4 }}
            >
              {requiresPasswordChange ? "Stel je eigen wachtwoord in" : "Meld u aan om uw uren te registreren"}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {!requiresPasswordChange && <TextField
                fullWidth
                label="E-mailadres"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
                autoFocus
                sx={{ mb: 2 }}
                inputProps={{
                  "aria-label": "E-mailadres",
                  maxLength: 256,
                }}
              />}

              <TextField
                fullWidth
                label={requiresPasswordChange ? "Nieuw wachtwoord" : "Wachtwoord"}
                type="password"
                value={requiresPasswordChange ? newPassword : password}
                onChange={(e) => requiresPasswordChange ? setNewPassword(e.target.value) : setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete={requiresPasswordChange ? "new-password" : "current-password"}
                autoFocus={requiresPasswordChange}
                helperText={requiresPasswordChange ? "15 tot 128 tekens" : undefined}
                sx={{ mb: requiresPasswordChange ? 2 : 3 }}
                inputProps={{
                  "aria-label": requiresPasswordChange ? "Nieuw wachtwoord" : "Wachtwoord",
                  maxLength: requiresPasswordChange ? 128 : 1024,
                  minLength: requiresPasswordChange ? 15 : undefined,
                }}
              />

              {requiresPasswordChange && <TextField fullWidth label="Herhaal nieuw wachtwoord" type="password"
                value={confirmation} onChange={event => setConfirmation(event.target.value)} required
                disabled={isLoading} autoComplete="new-password" inputProps={{ maxLength: 128 }} sx={{ mb: 3 }} />}

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
              >
                {isLoading ? "Even geduld..." : requiresPasswordChange ? "Opslaan en inloggen" : "Inloggen"}
              </Button>
              {requiresPasswordChange && <Button fullWidth startIcon={<ArrowBack />} disabled={isLoading} sx={{ mt: 1 }}
                onClick={() => { cancelPasswordChange(); setNewPassword(""); setConfirmation(""); setError(""); }}>
                Terug naar inloggen
              </Button>}
            </form>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 3 }}
            >
              © 2025 Buro C!TE - Timesheet
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
